import { ClinicAuthError } from "@auth/clinicAuthClient";
import { getActiveSession } from "@auth/sessionStore";

const detailOf = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object" || !("detail" in error))
    return undefined;
  const { detail } = error as { detail: unknown };
  return typeof detail === "string" ? detail : undefined;
};

const fromResponse = (response: Response, error: unknown): ClinicAuthError => {
  const detail = detailOf(error);
  if (response.status === 401)
    return new ClinicAuthError(
      "invalid_credentials",
      detail,
      response.status,
      detail,
    );
  return new ClinicAuthError(
    "unknown",
    detail ?? `HTTP ${response.status}`,
    response.status,
    detail,
  );
};

export async function request<T>(
  call: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  if (!getActiveSession())
    throw new ClinicAuthError("invalid_credentials", "No active session");

  let result;
  try {
    result = await call();
  } catch {
    throw new ClinicAuthError("network_error");
  }
  if (!result.data) throw fromResponse(result.response, result.error);
  return result.data;
}

/** {@link request} for endpoints with no body (e.g. a 204 DELETE). */
export async function requestVoid(
  call: () => Promise<{ error?: unknown; response: Response }>,
): Promise<void> {
  if (!getActiveSession())
    throw new ClinicAuthError("invalid_credentials", "No active session");

  let result;
  try {
    result = await call();
  } catch {
    throw new ClinicAuthError("network_error");
  }
  if (!result.response.ok) throw fromResponse(result.response, result.error);
}
